import { useState } from 'react'
import { Bell, X, Calendar, MessageCircle, Clock, Settings, CheckCheck, Trash2 } from 'lucide-react'
import { Drawer, DrawerContent, DrawerTitle } from '../ui/drawer'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import type { Notification, NotificationType } from './types'
import { useTranslation } from '../../i18n/LanguageContext'

interface NotificationDrawerProps {
  isOpen: boolean
  onClose: () => void
  notifications: Notification[]
  onMarkAsRead: (id: string) => void
  onMarkAllAsRead: () => void
  /** Remove every notification. The footer button shows only when this is given. */
  onClearAll?: () => void
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'meeting':
      return <Calendar className="w-4 h-4" />
    case 'chat':
      return <MessageCircle className="w-4 h-4" />
    case 'reminder':
      return <Clock className="w-4 h-4" />
    case 'system':
      return <Settings className="w-4 h-4" />
    default:
      return <Bell className="w-4 h-4" />
  }
}

const getNotificationColor = (type: NotificationType) => {
  switch (type) {
    case 'meeting':
      return 'text-blue-500 bg-blue-500/10'
    case 'chat':
      return 'text-green-500 bg-green-500/10'
    case 'reminder':
      return 'text-orange-500 bg-orange-500/10'
    case 'system':
      return 'text-purple-500 bg-purple-500/10'
    default:
      return 'text-gray-900 dark:text-foreground/80 bg-gray-500/10'
  }
}

const formatTimeAgo = (timestamp: Date, t: (key: string, vars?: Record<string, string | number>) => string) => {
  const now = new Date()
  const diff = now.getTime() - timestamp.getTime()
  const minutes = Math.floor(diff / (1000 * 60))
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (minutes < 1) return t('notifications.justNow')
  if (minutes < 60) return t('notifications.minutesAgo', { count: minutes })
  if (hours < 24) return t('notifications.hoursAgo', { count: hours })
  return t('notifications.daysAgo', { count: days })
}

export default function NotificationDrawer({
  isOpen,
  onClose,
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onClearAll,
}: NotificationDrawerProps) {
  const t = useTranslation()
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all')
  
  const unreadNotifications = notifications.filter(n => !n.isRead)
  const displayNotifications = activeTab === 'unread' ? unreadNotifications : notifications

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent direction="right" className="h-full w-full sm:w-[400px] sm:max-w-[90vw]">
        <div className="flex items-center justify-between border-b border-stroke-100 dark:border-border px-4 py-3">
          <DrawerTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            {t("notifications.title")}
            {unreadNotifications.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {unreadNotifications.length}
              </Badge>
            )}
          </DrawerTitle>
          <div className="flex items-center gap-2">
            {unreadNotifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMarkAllAsRead}
                className="h-8 text-xs"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                {t("notifications.markAllAsRead")}
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'text-primary border-b-2 border-primary bg-primary/5'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t("notifications.all")} ({notifications.length})
          </button>
          <button
            onClick={() => setActiveTab('unread')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'unread'
                ? 'text-primary border-b-2 border-primary bg-primary/5'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t("notifications.unread")} ({unreadNotifications.length})
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {displayNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 py-12 gap-4">
              <img
                src="/icons/NoNotifications.svg"
                alt=""
                className="w-44 sm:w-52 max-w-full drop-shadow-sm zoom-animation select-none pointer-events-none"
              />
              <div className="space-y-1 max-w-xs">
                <p className="text-base font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">
                  {activeTab === 'unread'
                    ? t('notifications.noUnread')
                    : t('notifications.noNotifications')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {activeTab === 'unread'
                    ? t('notifications.allCaughtUpDesc')
                    : t('notifications.noNotificationsDesc')}
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-stroke-100 dark:divide-border">
              {displayNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-muted/30 transition-colors cursor-pointer ${
                    !notification.isRead ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => {
                    if (!notification.isRead) {
                      onMarkAsRead(notification.id)
                    }
                    if (notification.actionUrl) {
                      // Handle navigation
                      console.log('Navigate to:', notification.actionUrl)
                    }
                  }}
                >
                  <div className="flex gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${getNotificationColor(notification.type)}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className={`text-sm font-medium ${!notification.isRead ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notification.title}
                        </h4>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-muted-foreground">
                            {formatTimeAgo(notification.timestamp, t)}
                          </span>
                          {!notification.isRead && (
                            <div className="w-2 h-2 rounded-full bg-primary" />
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {onClearAll && notifications.length > 0 && (
          <div className="border-t border-border p-3">
            <Button variant="outline" size="sm" onClick={onClearAll} className="w-full h-9 text-sm">
              <Trash2 className="w-4 h-4 mr-2" />
              {t("notifications.clearAll")}
            </Button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  )
}
